/**
 * DeepResearchChatStorage utility
 *
 * This module provides functions to store and retrieve chat IDs in session storage
 * for per-tab chat persistence. Each browser tab maintains its own chat session.
 */

import {v4 as uuidv4} from 'uuid';

// Storage key for DeepResearch chat ID
const DR_CHAT_ID_KEY = 'deep-research-chat-id';
const DR_STATUS_URL_KEY = 'deep-research-status-url';
const DR_USER_MESSAGE_KEY = 'deep-research-user-message';

/**
 * Generate a unique tab identifier that persists for the lifetime of this tab
 * This ensures each browser tab maintains its own chat history
 */
function getTabId(): string {
  // Check if we already have a tab ID in the window object
  if (!window.__deepResearchTabId) {
    // First time initialization - create a new UUID for this tab
    window.__deepResearchTabId = uuidv4();
  }
  return window.__deepResearchTabId;
}

/**
 * Get the Chat ID storage key specific to this browser tab
 */
function getChatIdStorageKey(): string {
  return `${DR_CHAT_ID_KEY}-${getTabId()}`;
}

/**
 * Get the status URL storage key specific to this browser tab
 */
function getStatusUrlStorageKey(): string {
  return `${DR_STATUS_URL_KEY}-${getTabId()}`;
}

/**
 * Get the user message storage key specific to this browser tab
 */
function getUserMessageStorageKey(): string {
  return `${DR_USER_MESSAGE_KEY}-${getTabId()}`;
}

/**
 * Save the current chat ID to session storage
 * Uses session storage to ensure per-tab persistence
 *
 * @param chatId The chat ID to save
 */
export function saveChatId(chatId: string): void {
  if (chatId) {
    const storageKey = getChatIdStorageKey();
    sessionStorage.setItem(storageKey, chatId);
  }
}

/**
 * Load saved chat ID from session storage
 * Returns undefined if no saved chat ID exists or if there's an error
 *
 * @returns The saved chat ID or undefined if none exists
 */
export function loadChatId(): string | undefined {
  const storageKey = getChatIdStorageKey();
  return sessionStorage.getItem(storageKey) || undefined;
}

/**
 * Clear the saved chat ID from session storage
 */
export function clearChatId(): void {
  const storageKey = getChatIdStorageKey();
  sessionStorage.removeItem(storageKey);
}

/**
 * Save the current status URL to session storage
 */
export function saveStatusUrl(statusUrl: string): void {
  const storageKey = getStatusUrlStorageKey();
  sessionStorage.setItem(storageKey, statusUrl);
}

/**
 * Load saved status URL from session storage
 */
export function loadStatusUrl(): string | undefined {
  const storageKey = getStatusUrlStorageKey();
  return sessionStorage.getItem(storageKey) || undefined;
}

/**
 * Clear the saved status URL from session storage
 */
export function clearStatusUrl(): void {
  const storageKey = getStatusUrlStorageKey();
  sessionStorage.removeItem(storageKey);
}

/**
 * Save the current user message to session storage
 */
export function saveUserMessage(userMessage: string): void {
  const storageKey = getUserMessageStorageKey();
  sessionStorage.setItem(storageKey, userMessage);
}

/**
 * Load saved user message from session storage
 */
export function loadUserMessage(): string | undefined {
  const storageKey = getUserMessageStorageKey();
  return sessionStorage.getItem(storageKey) || undefined;
}

/**
 * Clear the saved user message from session storage
 */
export function clearUserMessage(): void {
  const storageKey = getUserMessageStorageKey();
  sessionStorage.removeItem(storageKey);
}

// Add TypeScript interface declaration for the window object
declare global {
  interface Window {
    __deepResearchTabId?: string;
  }
}
